/**
 * Template Storage Utility
 *
 * Provides localStorage persistence for QR templates keyed by site ID.
 * Handles serialization, deserialization, and validation of stored templates.
 */

import { QrTemplate, DEFAULT_TEMPLATE } from '../types';

const STORAGE_KEY_PREFIX = 'qr-template-';

/**
 * Generates the localStorage key for a given site ID
 */
function getStorageKey(siteId: string): string {
  return `${STORAGE_KEY_PREFIX}${siteId}`;
}

/**
 * Validates that a parsed object has the expected QrTemplate shape.
 * Returns true if valid, false otherwise.
 */
function isValidTemplate(obj: unknown): obj is QrTemplate {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const template = obj as Record<string, unknown>;

  return (
    typeof template.color === 'string' &&
    typeof template.centerEnabled === 'boolean' &&
    typeof template.showBayLabel === 'boolean' &&
    (template.fontFamily === 'Topgolf' || template.fontFamily === 'ShieldSans') &&
    typeof template.fontSize === 'number' &&
    (template.logoDataUrl === null || typeof template.logoDataUrl === 'string') &&
    typeof template.centerPadding === 'number' &&
    (template.bayLabelColor === undefined || typeof template.bayLabelColor === 'string')
  );
}

/**
 * Loads a QR template from localStorage for the given site ID.
 * Returns the stored template if valid, or null if not found/invalid.
 *
 * @param siteId - The site ID to load the template for
 * @returns The stored template or null
 */
export function loadTemplate(siteId: string): QrTemplate | null {
  const key = getStorageKey(siteId);

  try {
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const parsed: unknown = JSON.parse(stored);

    if (!isValidTemplate(parsed)) {
      // Invalid stored data - remove it
      localStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    // JSON parse error or other issue - remove corrupted data
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Saves a QR template to localStorage for the given site ID.
 *
 * @param siteId - The site ID to save the template for
 * @param template - The template to save
 */
export function saveTemplate(siteId: string, template: QrTemplate): void {
  try {
    const key = getStorageKey(siteId);
    localStorage.setItem(key, JSON.stringify(template));
  } catch {
    // Storage quota exceeded or other error - silently fail
    console.warn('Failed to save QR template to localStorage');
  }
}

/**
 * Removes a stored QR template for the given site ID.
 *
 * @param siteId - The site ID to remove the template for
 */
export function removeTemplate(siteId: string): void {
  try {
    const key = getStorageKey(siteId);
    localStorage.removeItem(key);
  } catch {
    // Silently fail
  }
}

/**
 * Loads a template from storage, falling back to defaults for any missing fields.
 * This handles schema migrations when new fields are added to QrTemplate.
 *
 * @param siteId - The site ID to load the template for
 * @returns A complete template with defaults applied for missing fields
 */
export function loadTemplateWithDefaults(siteId: string): QrTemplate {
  const stored = loadTemplate(siteId);

  if (!stored) {
    return DEFAULT_TEMPLATE;
  }

  // Merge with defaults to handle any missing fields from older stored versions
  return {
    ...DEFAULT_TEMPLATE,
    ...stored,
  };
}
